import FluxUtils from 'flux/utils';
import Immutable from 'immutable';

import type { ActionType } from 'sharedreact/actions/generic/actions';
import AuthenticationConstants from 'sharedreact/constants/AuthenticationConstants';
import TablePreferencesConstants from 'sharedreact/constants/TablePreferencesConstants';
import UserPreferencesConstants from 'sharedreact/constants/UserPreferencesConstants';
import SortOrder from 'sharedreact/data/SortOrder';
import TablePreferences from 'sharedreact/data/TablePreferences';
import TableSortPreferences from 'sharedreact/data/TableSortPreferences';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';

class TablePreferencesStore extends FluxUtils.ReduceStore<TablePreferences, ActionType> {
  tablePrefsFromServer?: TablePreferences; 

  constructor () {
    super(AppDispatcher);

    this.tablePrefsFromServer = undefined;
  }

  override getInitialState (): TablePreferences {
    return new TablePreferences();
  }


  isDirty () {
    return !Immutable.is(this.getState(), this.tablePrefsFromServer);
  }

  
  clearDirty() {
    this.tablePrefsFromServer = this.getState();
  }

  override reduce (state: TablePreferences, action: ActionType): TablePreferences {
    let retval = state;

    switch (action.actionType) {
      case AuthenticationConstants.LOGOUT_SUCCESS: {
        // clear the preferences
        retval = this.getInitialState();
        break;
      }
      case TablePreferencesConstants.SET_SELECTED_COLUMNS: {
        retval = retval.setSelectedColumns(action.tableName, action.selectedColumns);
        break;
      }
      case TablePreferencesConstants.SET_COLUMN_WIDTHS: {
        retval = retval.mergeColumnWidths(action.tableName, action.columnWidths);
        break;
      }
      case TablePreferencesConstants.SET_COLUMN_ORDER: {
        retval = retval.setColumnOrder(action.tableName, action.columnOrder);
        break;
      }
      case TablePreferencesConstants.SET_TABLE_SORTORDER: {
        retval = retval.setTableSortOrder(action.tableName, action.sortOrder);
        break;
      }
      case TablePreferencesConstants.GET_SUCCESS: {
        if (action.preferences != null) {
          // Convert indexed js objects to Lists and others to Maps
          let newPreferences = Immutable.fromJS(action.preferences);

          // Convert selectedColumns to Map of string to Set i/o List
          let selectedColumns = newPreferences.get('selectedColumns');
          if (selectedColumns != null) {
            const keys = selectedColumns.keySeq().toArray();
            for (const key of keys) {
              const columnSet = Immutable.Set(selectedColumns.get(key));
              selectedColumns = selectedColumns.set(key, columnSet);
            }
            newPreferences = newPreferences.set('selectedColumns', selectedColumns);
          }

          // Convert sorting to Map of string to TableSortPreferences i/o Map
          let sorting = newPreferences.get('sorting');
          if (sorting != null) {
            const keys = sorting.keySeq().toArray();
            for (const key of keys) {
              const prefs = new TableSortPreferences(sorting.get(key));
              sorting = sorting.set(key, prefs);
            }
            newPreferences = newPreferences.set('sorting', sorting);
          }

          retval = new TablePreferences(newPreferences);
        } else {
          // no preferences found
          retval = this.getInitialState();
        }
        this.tablePrefsFromServer = retval;
        break;
      }
      case UserPreferencesConstants.CLEAR_SUCCESS: {
        const _state = this.getInitialState();

        this.tablePrefsFromServer = _state;

        return _state;
      }
    }

    return retval;
  }

  getSelectedColumns (tableName: string): Immutable.Set<string> | undefined {
    return this.getState().selectedColumns.get(tableName);
  }

  getColumnWidths (tableName: string): Immutable.Map<string, string> | undefined {
    return this.getState().columnWidths.get(tableName);
  }

  getColumnOrder (tableName: string): Immutable.List<string> | undefined {
    return this.getState().columnOrder.get(tableName);
  }

  getTableSortOrder (tableName: string, defaultValue: SortOrder): SortOrder {
    const prefs = this.getState().sorting.get(tableName);
    if (prefs == null) {
      return defaultValue;
    } else {
      return new SortOrder({keyword: prefs.activeKeyword, ascending: prefs.getAscending(prefs.activeKeyword)});
    }
  }

  getSortPreferences (tableName: string): TableSortPreferences  | undefined {
    return this.getState().sorting.get(tableName);
  }
}

export default new TablePreferencesStore();
